import React, { useMemo } from 'react';
import type { ViewProps } from 'react-native';
import { StyleSheet, View } from 'react-native';
import {
  BodyText,
  Button,
  COLORS,
  Ledger as LedgerComponent,
  SectionHeader,
  SPACING,
  VStack,
} from '@sweetgreen/garnish';

import { LineItemCard } from '@order/components';
import { useLocalizationContext } from '@order/Localization';

import { useLedgerItems } from '../../../../../components/Bag/Bag.hooks';
import type { PartialLineItem } from '../../../../../graphql/partials';
import type { Ledger } from '../../../../../graphql/types';
import { applyCredit, applyTip } from '../../../Checkout.utils';
import { CheckoutLayoutBlock } from '../../CheckoutLayout';

export const CheckoutOrderBlock = (props: CheckoutOrderBlockProps) => {
  const { t } = useLocalizationContext();
  const {
    lineItems = [],
    submitting,
    onSubmitOrder,
    credit,
    ledger,
    tip = 0,
    isDelivery,
    isSubmitActive,
  } = props;
  const withAppliedCredit = applyCredit(ledger, credit);
  const showTip = isDelivery;

  const memoizedLedger = useMemo(() => {
    const withTip = isDelivery ? applyTip(withAppliedCredit, tip) : {};

    return { ...withAppliedCredit, ...withTip };
  }, [isDelivery, tip, withAppliedCredit]);

  const ledgerItems = useLedgerItems({
    ledger: memoizedLedger,
    showTip,
    showTotal: true,
    showTaxes: true,
    showReward: true,
  });

  return (
    <CheckoutLayoutBlock {...props}>
      <SectionHeader heading={t('general.your-order')} />

      {/* ========== ========== ========== ========== */}

      <VStack customSpacing={0} divider={true} dividerColor={COLORS.LIGHT}>
        {lineItems.map((lineItem) => (
          <LineItemCard key={lineItem.id} lineItem={lineItem} />
        ))}
      </VStack>

      {/* ========== ========== ========== ========== */}

      <LedgerComponent
        style={styles.ledger}
        hasTopBorder
        items={ledgerItems}
        testID="checkout.ledger"
      />

      {/* ========== ========== ========== ========== */}

      <View style={styles.orderButtonWrapper}>
        <Button
          onPress={onSubmitOrder}
          isLoading={submitting}
          size="large"
          palette="primary"
          testID="checkout.place-order"
          disabled={!isSubmitActive}
        >
          {t('checkout.place-order')}
        </Button>
      </View>

      {/* ========== ========== ========== ========== */}

      <VStack customSpacing={SPACING.medium} style={styles.helperTextsStack}>
        <CheckoutOrderBlockHelperText
          title={t('checkout.allergens-disclaimer')}
          text={t('checkout.allergens-disclaimer-message')}
        />
        {isDelivery ? (
          <CheckoutOrderBlockHelperText
            title={t('checkout.delivery-provider')}
            text={t('checkout.delivery-provider-message')}
          />
        ) : null}
        <CheckoutOrderBlockHelperText
          title={t('checkout.cancellation-policy')}
          text={t('checkout.cancellation-policy-message')}
        />
      </VStack>
    </CheckoutLayoutBlock>
  );
};

// ─── TYPES ──────────────────────────────────────────────────────────────────────

type CheckoutOrderBlockProps = {
  readonly lineItems?: readonly PartialLineItem[];
  readonly ledger?: Partial<Ledger>;
  readonly isDelivery: boolean;
  readonly isSubmitActive: boolean;
  readonly submitting: boolean;
  readonly onSubmitOrder: () => void;
  readonly credit: number;
  readonly tip?: number;
} & Omit<ViewProps, 'children'>;

type CheckoutOrderBlockInfoMessageProps = {
  readonly title?: string;
  readonly text: string;
} & Omit<ViewProps, 'children'>;

// ─── STYLES ──────────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  ledger: {
    marginTop: SPACING.small,
  },
  orderButtonWrapper: {
    paddingTop: SPACING.xsmall,
  },
  helperTextsStack: {
    paddingTop: SPACING.xlarge,
  },
});

// ─── SUBCOMPONENTS ──────────────────────────────────────────────────────────────

export const CheckoutOrderBlockHelperText = (
  props: CheckoutOrderBlockInfoMessageProps,
) => {
  const { title, text, ...restProps } = props;

  return (
    <View {...restProps}>
      {title ? (
        <BodyText size={5} bold>
          {title}
        </BodyText>
      ) : null}
      <BodyText size={5}>{text}</BodyText>
    </View>
  );
};
